// notificacion.actions.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { NavigateFunction } from "react-router-dom";
import { UseMutationsParams, AlertSeverityEnum } from "@/shared/interfaces";
import { erpApi } from "@/shared/axios";
import { toApiError } from "@/shared/utils/general";
import { useUiStore } from "@/store/ui";
import { getUrlParamsFetch, handleAxiosError } from "@/shared/utils";
import { urlBackend } from "@/shared/constants";
import {
  Notificacion,
  NotificacionForm,
  NotificacionParams,
  NotificacionPagingResp,
} from "@/erp/controlcambios/notificacion/types/notificacion.interface";

const { get, post, put, remove } = erpApi();
const BASE = `${urlBackend}api/v1/cambios/notificaciones`;

// listar
export const useFetchGetNotificaciones = (
  params?: NotificacionParams,
  enabled = true
) =>
  useQuery({
    queryKey: ["notificaciones", ...Object.values(params ?? {})],
    queryFn: async () => getNotificaciones(params),
    enabled,
  });

export const getNotificaciones = async (params?: NotificacionParams) => {
  const q = getUrlParamsFetch(params ?? {});
  return (await get<NotificacionPagingResp>(`${BASE}?${q}`, true)) as unknown as NotificacionPagingResp;
};

// obtener por id
export const useFetchGetByIdNotificacion = (id: string) =>
  useQuery({
    queryKey: ["notificacion", id],
    queryFn: async () => getByIdNotificacion(id),
    refetchOnWindowFocus: false,
  });

export const getByIdNotificacion = async (id: string) =>
  await get<Notificacion>(`${BASE}/${id}`, true);

// crear
export const useFetchPostNotificacion = ({
  returnUrl,
  navigate,
  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setAlerta = useUiStore.getState().setAlert;
  return useMutation({
    mutationFn: (data: NotificacionForm) => postNotificacion(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notificaciones"] });
      navigate && returnUrl && navigate(returnUrl);
      enableSuccessAlert &&
        setAlerta &&
        setAlerta(
          true,
          customSuccessMessage ?? "Notificación creada correctamente",
          AlertSeverityEnum.success
        );
    },
    onError: (error) => toast.error(toApiError(error).message),
  });
};

export const postNotificacion = async (data: NotificacionForm) =>
  await post<Notificacion>(BASE, data, true);

// actualizar
export const useFetchPutNotificacion = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();
  const setAlerta = useUiStore.getState().setAlert;
  return useMutation({
    mutationFn: putNotificacion,
    onSuccess: (_d, variables) => {
      queryClient.invalidateQueries({ queryKey: ["notificaciones"] });
      queryClient.invalidateQueries({ queryKey: ["notificacion", variables.id] });
      setAlerta(true, "Notificación actualizada correctamente", AlertSeverityEnum.success);
      navigate(returnUrl);
    },
    onError: (error) => handleAxiosError(error),
  });
};

export const putNotificacion = async ({
  id,
  data,
}: {
  id: string;
  data: Omit<NotificacionForm, "notificacionId">;
}) => await put<Notificacion>(`${BASE}/${id}`, data, true);

// eliminar
export const useFetchDeleteNotificacion = () => {
  const queryClient = useQueryClient();
  const setAlerta = useUiStore.getState().setAlert;
  return useMutation({
    mutationFn: (id: string) => deleteNotificacion(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notificaciones"] });
      setAlerta(true, "Notificación eliminada correctamente", AlertSeverityEnum.success);
    },
    onError: (error) => toast.error(toApiError(error).message),
  });
};

export const deleteNotificacion = async (id: string) =>
  await remove<Notificacion>(`${BASE}/${id}`, true);
